import { useState } from 'react';
import { useFormikContext } from 'formik';
import {
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import { pluralize } from '@/react/common/string-utils';

import { confirm } from '@@/modals/confirm';

import { ArtifactFile, FormValues } from '../../types';

import { assignFileToArtifact, filesEvictedByDrop } from './artifactValues';
import { decodeArtifactDropId, decodeFileDragId } from './dragId';

/* pointer must move this far before a drag starts, so a plain click/check keeps working */
const DRAG_ACTIVATION_DISTANCE_PX = 8;

/** Owns the dnd-kit sensors/handlers for dropping a source file onto an artifact. */
export function useArtifactDrag(activate: (index: number) => void) {
  const { values, setValues } = useFormikContext<FormValues>();
  const [draggedFile, setDraggedFile] = useState<ArtifactFile>();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    })
  );

  return {
    sensors,
    draggedFile,
    handleDragStart,
    handleDragEnd,
    handleDragCancel: () => setDraggedFile(undefined),
  };

  function handleDragStart({ active }: DragStartEvent) {
    setDraggedFile(decodeFileDragId(active.id));
  }

  async function handleDragEnd({ active, over }: DragEndEvent) {
    setDraggedFile(undefined);

    if (!over) {
      return;
    }

    const file = decodeFileDragId(active.id);
    const targetIndex = decodeArtifactDropId(over.id);
    if (!file || targetIndex === undefined) {
      return;
    }

    const evicted = filesEvictedByDrop(values, targetIndex, file);
    if (evicted.length > 0 && !(await confirmReplace(evicted.length))) {
      return;
    }

    setValues(assignFileToArtifact(values, targetIndex, file));
    activate(targetIndex);
  }

  function confirmReplace(count: number) {
    return confirm({
      title: 'Replace the artifact’s files?',
      message: `With the Edge stack engine, an artifact can only use files from one source and ref, so this removes its ${count} current ${pluralize(count, 'file')}.`,
    });
  }
}
